import { isValidEmail, isValidURL } from './inputValidation';

/**
 * Validates funder form fields
 * Only company name is required, all other fields are optional
 */
export const validateFunderFormField = (field, value) => {
  // Company name is required
  if (field === 'name') {
    if (!value || value.trim() === '') {
      return 'Company/Fund Name is required';
    }
    return '';
  }
  
  // All other fields are optional - only validate if they have content
  if (!value || value === '') return '';
  
  switch (field) {
    case 'contact_email':
      return !isValidEmail(value) ? 'Please enter a valid email address' : '';
      
    case 'website':
      return !isValidURL(value) ? 'Please enter a valid URL (e.g., https://example.com)' : '';
      
    case 'linkedin':
      return !isValidURL(value) ? 'Please enter a valid LinkedIn URL' : '';
      
    case 'twitter':
      return value.startsWith('@') ? '' : 'Twitter handle should start with @';
      
    case 'min_cheque_size':
    case 'max_cheque_size':
      if (value && isNaN(parseFloat(value.replace(/[$,]/g, '')))) {
        return 'Please enter a valid amount';
      }
      return '';
      
    default:
      return '';
  }
};

/**
 * Validates the entire funder form
 * Returns object with field errors
 */
export const validateFunderForm = (formData) => {
  const errors = {};

  // NEW REQUIRED FIELDS VALIDATION
  if (!formData.firstName || formData.firstName.trim() === '') {
    errors.firstName = 'First Name is required';
  }

  if (!formData.lastName || formData.lastName.trim() === '') {
    errors.lastName = 'Last Name is required';
  }

  if (!formData.personalEmail || formData.personalEmail.trim() === '') {
    errors.personalEmail = 'Personal Email is required';
  } else if (!isValidEmail(formData.personalEmail)) {
    errors.personalEmail = 'Please enter a valid email address';
  }

  if (!formData.password || formData.password.trim() === '') {
    errors.password = 'Password is required';
  } else if (formData.password.length < 8) {
    errors.password = 'Password must be at least 8 characters long';
  } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
    errors.password = 'Password must contain at least one lowercase letter, one uppercase letter, and one number';
  }

  if (!formData.confirmPassword || formData.confirmPassword.trim() === '') {
    errors.confirmPassword = 'Please confirm your password';
  } else if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  // Business phone is optional but validate if provided
  if (formData.businessPhone && formData.businessPhone.trim() !== '') {
    if (!isValidPhone(formData.businessPhone)) {
      errors.businessPhone = 'Please enter a valid phone number';
    }
  }

  // EXISTING BUSINESS FIELDS VALIDATION
  if (!formData.name || formData.name.trim() === '') {
    errors.name = 'Company/Fund name is required';
  }

  // Validate optional fields only if they have values
  if (formData.contact_email && formData.contact_email.trim() !== '') {
    if (!isValidEmail(formData.contact_email)) {
      errors.contact_email = 'Please enter a valid email address';
    }
  }
  
  if (formData.website && formData.website.trim() !== '') {
    if (!isValidUrl(formData.website)) {
      errors.website = 'Please enter a valid URL';
    }
  }
  
  if (formData.linkedin && formData.linkedin.trim() !== '') {
    if (!isValidUrl(formData.linkedin)) {
      errors.linkedin = 'Please enter a valid LinkedIn URL';
    }
  }
  
  if (formData.twitter && formData.twitter.trim() !== '') {
    if (!formData.twitter.startsWith('@')) {
      errors.twitter = 'Twitter handle should start with @';
    }
  }

  return errors;
};

// Helper function for phone validation (not imported, so keep this one)
const isValidPhone = (phone) => {
  // Basic phone validation - allows various formats
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  const cleanPhone = phone.replace(/[\s\-\(\)\.]/g, '');
  return phoneRegex.test(cleanPhone);
};

/**
 * Checks if the form has any validation errors
 */
export const hasFormErrors = (errors) => {
  return Object.keys(errors).length > 0;
};
